import { defineStore } from 'pinia'
import { useStorage } from '@vueuse/core'
import * as Sentry from '@sentry/vue'
import AuthService from '@/api/AuthService'
import { useUserStore } from './user'
import { trackEvent } from '@/config/analytics'
import { identifyHotjarUser, trackHotjarEvent, resetHotjarUser } from '@/config/hotjar'
import {
    clearLegacyTableFilters,
    clearRetainedTableFilters,
    expireTableFilters,
} from '@/helpers/retainedTableFilters'

const DEFAULT_LANDING_PATH = '/app/home'

function intendedPath(intended) {
    if (typeof intended !== 'string' || intended === '') return DEFAULT_LANDING_PATH

    try {
        const target = new URL(intended, window.location.origin)
        return target.origin === window.location.origin
            ? target.pathname + target.search + target.hash
            : DEFAULT_LANDING_PATH
    } catch {
        return DEFAULT_LANDING_PATH
    }
}

export const useAuthStore = defineStore('auth', {
    state: () => ({
        userId: useStorage('auth_user_id', null),
        userType: useStorage('auth_user_type', null), // 'external' | 'internal'
        loading: false,
        error: null,
    }),

    getters: {
        isAuthenticated: (state) => !!state.userId,
        hasError: (state) => !!state.error,
        currentUserType: (state) => state.userType,
    },

    actions: {
        async login(credentials) {
            this.loading = true
            this.error = null

            try {
                const { data } = await AuthService.login(credentials)

                expireTableFilters()
                this.setUserId(data.id)
                this.setUserType(data.user_type || 'external')

                Sentry.setUser({
                    id: data.id,
                    user_type: this.userType,
                    email: data.email,
                    permissions: data.permissions?.length || 0,
                })

                const userStore = useUserStore()
                userStore.setUserData(data)
                identifyHotjarUser(data, this.userType)
                trackEvent('login')
                trackHotjarEvent('login')
                // unfortunately we have to delay the redirect until after the user data is set, or we lose the ability to identify the user in Hotjar
                await new Promise((r) => setTimeout(r, 300))
                window.location.assign(intendedPath(data.intended))

                return data
            } catch (error) {
                console.error('Error during login (stores/auth/login):', error)
                this.error = error.response?.data?.message || 'Authentication failed'
                throw error
            } finally {
                this.loading = false
            }
        },

        async loginInternal(credentials) {
            this.loading = true
            this.error = null

            try {
                const { data } = await AuthService.loginInternal(credentials)

                expireTableFilters()
                this.setUserId(data.id)
                this.setUserType(data.user_type || 'internal')

                Sentry.setUser({
                    id: data.id,
                    user_type: this.userType,
                    email: data.email,
                    permissions: data.permissions?.length || 0,
                })

                const userStore = useUserStore()
                userStore.setUserData(data)
                window.location.assign(intendedPath(data.intended))

                return data
            } catch (error) {
                console.error('Error during internal login (stores/auth/loginInternal):', error)
                this.error = error.response?.data?.message || 'Authentication failed'
                throw error
            } finally {
                this.loading = false
            }
        },

        async logout() {
            clearRetainedTableFilters()
            clearLegacyTableFilters()

            try {
                await AuthService.logout()

                const userStore = useUserStore()
                const loginPath = userStore.isInternalUser ? '/internal' : '/login'

                trackEvent('logout')
                trackHotjarEvent('logout')
                resetHotjarUser()
                this.resetAuthData()
                userStore.resetUser()
                this.setUserId(null)
                this.setUserType(null)
                Sentry.setUser(null)

                // unfortunately we have to delay the redirect until after the user data is set, or we lose the ability to identify the user in Hotjar
                await new Promise((r) => setTimeout(r, 300))
                window.location.pathname = loginPath
            } catch (error) {
                this.resetAuthData()
                const userStore = useUserStore()
                userStore.resetUser()

                console.error('Logout error:', error)
            }
        },

        async fetchCurrentUser() {
            try {
                const { data } = await AuthService.getAuthenticatedUser()
                this.setUserId(data.id)
                this.setUserType(data.user_type || 'external')
                const userStore = useUserStore()
                userStore.setUserData(data)
            } catch (error) {
                console.error('Error fetching current user (stores/auth/fetchCurrentUser):', error)
                this.setUserId(null)
            }
        },

        async register(credentials) {
            this.loading = true
            try {
                const { invite, name, password, preferred_locale } = credentials

                const { data } = await AuthService.register(
                    { name, password, preferred_locale },
                    invite,
                )

                expireTableFilters()
                this.setUserId(data.id)
                this.setUserType(data.user_type || 'external')

                Sentry.setUser({
                    id: data.id,
                    email: data.email,
                    permissions: data.permissions?.length || 0,
                })

                const userStore = useUserStore()
                userStore.setUserData(data)
                identifyHotjarUser(data, 'external')
                trackEvent('sign_up')
                trackHotjarEvent('sign_up')
                // unfortunately we have to delay the redirect until after the user data is set, or we lose the ability to identify the user in Hotjar
                await new Promise((r) => setTimeout(r, 300))
                window.location.assign(intendedPath(data.intended))
                return true
            } catch (error) {
                console.error('Error during registration (stores/auth/register):', error)
                this.error = error.response?.data?.message || 'Registration failed'
                throw error
            } finally {
                this.loading = false
            }
        },

        async internalRegister(credentials) {
            this.loading = true
            try {
                const { invite, name, password, preferred_locale } = credentials

                const { data } = await AuthService.internalRegister(
                    { name, password, preferred_locale },
                    invite,
                )

                expireTableFilters()
                this.setUserId(data.id)
                this.currentUserType = 'internal'

                const userStore = useUserStore()
                userStore.setUserData(data)
                window.location.pathname = '/app/home'
                return true
            } catch (error) {
                console.error(
                    'Error during internal registration (stores/auth/internalRegister):',
                    error,
                )
                this.error = error.response?.data?.message || 'Registration failed'
                throw error
            } finally {
                this.loading = false
            }
        },

        async requestPasswordReset(email) {
            this.loading = true
            try {
                await AuthService.forgotPassword(email)
                return true
            } catch (error) {
                console.error(
                    'Error requesting password reset (stores/auth/requestPasswordReset):',
                    error,
                )
                this.error = error.response?.data?.message || 'Password reset request failed'
                throw error
            } finally {
                this.loading = false
            }
        },

        async setNewPassword(credentials) {
            this.loading = true
            try {
                const { email, token, password, passwordConfirm } = credentials
                const { data } = await AuthService.resetPassword(
                    email,
                    token,
                    password,
                    passwordConfirm,
                )
                expireTableFilters()
                this.setUserId(data.id)

                Sentry.setUser({
                    id: data.id,
                    email: data.email,
                    permissions: data.permissions?.length || 0,
                })

                const userStore = useUserStore()
                userStore.setUserData(data)
                window.location.pathname = '/app/home'
            } catch (error) {
                console.error('Error setting new password (stores/auth/setNewPassword):', error)
                this.error = error.response?.data?.message || 'Password reset failed'
                throw error
            } finally {
                this.loading = false
            }
        },

        async updatePassword(credentials) {
            this.loading = true
            try {
                const { current_password, password, password_confirmation } = credentials
                await AuthService.updatePassword(current_password, password, password_confirmation)
                return true
            } catch (error) {
                console.error('Error updating password (stores/auth/updatePassword):', error)
                this.error = error.response?.data?.message || 'Password update failed'
                throw error
            } finally {
                this.loading = false
            }
        },

        resetAuthData() {
            this.setUserId(null)
            this.error = null
            this.loading = false
        },

        setUserId(id) {
            this.userId = id
        },
        setUserType(type) {
            this.userType = type
        },
    },
})
